import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateReactionRequest,
  DeleteReactionRequest,
  SwitchReactionRequest,
  ViewReactionsRequest,
  ViewReactionsResponse
} from './reaction.types';

import {
  requestCreateReaction,
  requestDeleteReaction,
  requestSwitchReaction,
  requestViewReactions
} from './reaction.actions';

import { getCreateReactionRequest, getDeleteReactionRequest, getViewReactionsRequest } from './reaction.endpoints';

import { setReactions, switchReaction } from '../../slices/programme/tribe/tribe.slice';
import { addSystemNotice } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performCreateReactionRequest({ payload }: SagaPayload<CreateReactionRequest>) {
  const { requestData, onSuccess, onError } = payload;

  try {
    const [endpoint, options] = getCreateReactionRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));

    if (onError) {
      yield call(onError, undefined);
    }
  }
}

export function* watchForCreateReactionRequest() {
  yield takeLatest(requestCreateReaction.type, performCreateReactionRequest);
}

export function* performViewReactionsRequest({ payload }: SagaPayload<ViewReactionsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewReactionsRequest(requestData);

    const { data }: AxiosResponse<ViewReactionsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setReactions(data.reactions));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewReactionsRequest() {
  yield takeLatest(requestViewReactions.type, performViewReactionsRequest);
}

export function* performDeleteReactionRequest({ payload }: SagaPayload<DeleteReactionRequest>) {
  const { requestData, onSuccess, onError } = payload;

  try {
    const [endpoint, options] = getDeleteReactionRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));

    if (onError) {
      yield call(onError, undefined);
    }
  }
}

export function* watchForDeleteReactionRequest() {
  yield takeLatest(requestDeleteReaction.type, performDeleteReactionRequest);
}

export function* performSwitchReactionRequest({ payload }: SagaPayload<SwitchReactionRequest>) {
  const { requestData } = payload;
  const { postExternalReference, oldReaction, newReaction, programmeExternalReference, memberExternalReference } =
    requestData;

  yield put(switchReaction({ postExternalReference, oldReaction, newReaction }));

  try {
    const deleteOptions = getDeleteReactionRequest({
      reaction: oldReaction,
      memberExternalReference,
      postExternalReference
    });

    yield call(axios, ...deleteOptions);

    const createOptions = getCreateReactionRequest({
      reaction: newReaction,
      postExternalReference,
      programmeExternalReference
    });

    yield call(axios, ...createOptions);
  } catch (error) {
    yield put(
      switchReaction({
        postExternalReference,
        oldReaction: newReaction,
        newReaction: oldReaction
      })
    );

    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForSwitchReactionRequest() {
  yield takeLatest(requestSwitchReaction.type, performSwitchReactionRequest);
}

export default function* reactionSaga() {
  yield all([
    watchForCreateReactionRequest(),
    watchForViewReactionsRequest(),
    watchForDeleteReactionRequest(),
    watchForSwitchReactionRequest()
  ]);
}
